import { ErrorResponse, UserManager, WebStorageStateStore } from 'oidc-client-ts';

// Fall back to the page's own origin when the URLs aren't baked in at build
// time. The deployed-dev bundle ships with EMPTY SERVER_URL/CLIENT_URL so ONE
// build serves both mowazafdev and mowazafdev-linux (shared dev DB). Local
// split-port dev and prod keep their explicit values in the env file, so the
// fallback never fires there.
const serverUrl = process.env.SERVER_URL || window.location.origin;
const clientUrl = process.env.CLIENT_URL || window.location.origin;

// mainclient is a Public SPA; the backend does NOT grant it a refresh token
// (see Mowazaf.Server/Services/Authorization/ClientsSeeder.cs).
// Token renewal is driven exclusively by the 401 interceptor in
// utils/requestHandling.ts — signinSilent() uses a hidden iframe with
// prompt=none against the backend's session cookie.
//
// NOTE: iframe silent-renew requires same-origin (or first-party) access to
// the IdP's session cookie. In production the backend serves both the API
// and the SPA, so origins match. If this ever changes (split domains),
// silent renewal will break in Safari/Chrome under third-party cookie
// restrictions and needs to be revisited.
export const userManager = new UserManager({
    authority: serverUrl,
    client_id: "mainclient",
    redirect_uri: clientUrl + "/logincallback",
    silent_redirect_uri: clientUrl + "/silent-renew",
    post_logout_redirect_uri: clientUrl + "/logoutcallback",
    response_type: "code",
    scope: "api1 email profile",
    // Disabled intentionally. With 9.5-year access-token lifetimes,
    // proactive renewal rarely fires and creates races with reactive 401
    // handling (e.g. addAccessTokenExpired firing mid-renewal). Reactive
    // refresh in the 401 interceptor is the single renewal path.
    automaticSilentRenew: false,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    extraQueryParams: {},
});

export default userManager;

// Cross-tab signaling. BroadcastChannel is unavailable in Safari private mode
// and some embedded webviews; callers must tolerate a null channel. The
// storage-event fallback (persist:root removal) is wired in authLifecycle.ts.
let authChannel: BroadcastChannel | null = null;
try {
    if (typeof BroadcastChannel !== 'undefined') {
        authChannel = new BroadcastChannel('mowazaf-auth');
    }
} catch {
    authChannel = null;
}

export type AuthChannelMessage =
    | { type: 'logout'; reason: string; at: number }
    | { type: 'user-loaded'; at: number };

export function broadcastLogout(reason: string): void {
    try {
        authChannel?.postMessage({ type: 'logout', reason, at: Date.now() });
    } catch {
        // ignore
    }
}

export function broadcastUserLoaded(): void {
    try {
        authChannel?.postMessage({ type: 'user-loaded', at: Date.now() });
    } catch {
        // ignore
    }
}

export function subscribeAuthChannel(handler: (msg: AuthChannelMessage) => void): () => void {
    if (!authChannel) return () => {};
    const listener = (e: MessageEvent) => {
        try { handler(e.data as AuthChannelMessage); } catch {
            // ignore handler errors — cross-tab messages are advisory
        }
    };
    authChannel.addEventListener('message', listener);
    return () => authChannel?.removeEventListener('message', listener);
}

// Classify a renewal/boot-time auth error as "hard" (session is truly gone —
// force full logout + re-auth) vs "transient" (network, timeout, 5xx — user
// should stay logged in; the next API call will try renewal again).
//
// Only these OIDC error codes prove the IdP session is unusable. Everything
// else (iframe timeout, CORS, DNS, 5xx from /connect/authorize, fetch aborts)
// is treated as transient — forcing logout on those kicks users out on flaky
// wifi, during a backend restart, or during any 10-second hiccup.
export function isHardAuthFailure(err: unknown): boolean {
    if (!err) return false;
    // oidc-client-ts sets `name === 'ErrorResponse'` as its class marker
    const name = (err as { name?: unknown })?.name;
    const code = (err as ErrorResponse)?.error;
    const isResponseError = err instanceof ErrorResponse || name === 'ErrorResponse';
    if (!isResponseError || typeof code !== 'string') return false;
    return (
        code === 'login_required' ||
        code === 'interaction_required' ||
        code === 'account_selection_required' ||
        code === 'consent_required' ||
        code === 'invalid_grant'
    );
}

export async function getUserAuthInfo() {
    const user = await userManager.getUser();
    return user;
}

// Poll until OIDC user is available or timeout reached
export async function waitForUserAuth(timeoutMs: number = 3000, intervalMs: number = 50) {
    const start = Date.now();
    let user = await getUserAuthInfo();
    if (user && user.access_token) return user;

    while (Date.now() - start < timeoutMs) {
        await new Promise((res) => setTimeout(res, intervalMs));
        user = await getUserAuthInfo();
        if (user && user.access_token) return user;
    }
    return user;
}

export async function isAuthenticated() {
    const token = await getAccessToken();
    return !!token;
}

export async function handleOAuthCallback(callbackUrl: any) {
    try {
        const user = await userManager.signinCallback(callbackUrl);
        return user;
    } catch (e) {
        console.log(`error while handling oauth callback: ${e}`);
    }
}

export async function handleSilentRenewCallback() {
    try {
        await userManager.signinSilentCallback();
    } catch (e) {
        console.log(`error while handling silent renew callback: ${e}`);
    }
}

export function sendOAuthRequest() {
    return userManager.signinRedirect();
}

// Reactive renewal — called from the 401 interceptor. Under the current
// mainclient configuration this runs an iframe against /connect/authorize
// with prompt=none; success requires a live backend session cookie.
export async function renewToken() {
    const user = await userManager.signinSilent();
    return user;
}

export async function getAccessToken() {
    const user = await getUserAuthInfo();
    return user?.access_token;
}

// Deliberate sign-out — ends the backend IdP session. Use this for the
// "Sign out" button. For involuntary logout (expired session, refresh
// failure), call forceLogout() instead.
export async function logout() {
    // Best-effort: clear this browser's web-push token server-side so a shared
    // device stops receiving this user's push after sign-out. Bounded and
    // fully swallowed — sign-out must never hang or fail on token cleanup.
    // Dynamic import avoids a static cycle (webPush → api/request → AuthService).
    try {
        const { clearWebPushToken } = await import('./webPush');
        await Promise.race([
            clearWebPushToken(),
            new Promise<void>((resolve) => setTimeout(resolve, 2000)),
        ]);
    } catch {
        // ignore — token cleanup is advisory
    }
    await userManager.clearStaleState();
    await userManager.signoutRedirect();
}

// --- Involuntary logout ---------------------------------------------------
//
// Called when the session is irrecoverable: silent-renew returned
// login_required, a 401 retry failed, boot-time renew threw. Idempotent: the
// first caller owns the teardown; subsequent callers return the same promise
// so we don't race multiple removeUser() + redirect() pairs.
//
// Order matters:
//   1. broadcast so other tabs begin teardown before ours tears down storage
//   2. persistor.pause() so the LOGOUT dispatch doesn't re-persist state
//   3. removeUser() clears OIDC storage AND fires addUserUnloaded, which
//      authLifecycle turns into the LOGOUT dispatch
//   4. persistor.purge() removes persist:root explicitly
//   5. clearStaleState() drops leftover PKCE/nonce entries
//   6. signinRedirect() with returnTo in state; if this throws (offline,
//      backend down) we fall back to navigating to "/"

let forceLogoutPromise: Promise<void> | null = null;
let loggedOutLock = false;

export function isLoggedOutLocked(): boolean {
    return loggedOutLock;
}

function onCallbackRoute(): boolean {
    try {
        const p = (window.location?.pathname || '').toLowerCase();
        return p.startsWith('/logincallback') || p.startsWith('/silent-renew') || p.startsWith('/logoutcallback');
    } catch {
        return false;
    }
}

// Exported so Callback.tsx can reuse the same rules when reading returnTo
// back out of the OIDC state object. Accepts unknown because the source
// (user.state.returnTo) is library-typed as any.
export function sanitizeReturnTo(path: unknown): string {
    if (!path || typeof path !== 'string') return '/';
    // Strip WHATWG-stripped characters (tab, CR, LF, NUL, other C0 controls).
    // Per URL spec, the URL parser removes these before interpreting the
    // scheme — so a path like "/\r/evil.com" would normalize to "//evil.com"
    // after parsing, bypassing a naive startsWith('//') check. Reject any
    // path containing them rather than trying to strip-then-validate.
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1f\x7f]/.test(path)) return '/';
    if (!path.startsWith('/')) return '/';
    // Block protocol-relative (//evil.com) and backslash variants that some
    // browsers normalize to a scheme. Also block %2f / %5c encoded variants
    // (case-insensitive).
    if (path.startsWith('//') || path.startsWith('/\\')) return '/';
    if (/^\/(%2f|%5c)/i.test(path)) return '/';
    return path;
}

export function forceLogout(reason: string): Promise<void> {
    if (forceLogoutPromise) return forceLogoutPromise;
    if (onCallbackRoute()) return Promise.resolve();

    try {
        const store = (globalThis as any).__APP_STORE__;
        if (store && !store.getState().user?.isLoggedIn) {
            // Nothing to tear down — avoid redirecting an already-anonymous
            // visitor to the IdP login screen.
            return Promise.resolve();
        }
    } catch {
        // If the store isn't reachable, continue; redirect is still safe.
    }

    loggedOutLock = true;
    console.warn(`[auth] forceLogout: ${reason}`);
    broadcastLogout(reason);

    forceLogoutPromise = (async () => {
        const persistor = (globalThis as any).__APP_PERSISTOR__;
        const returnTo = sanitizeReturnTo(
            (window.location?.pathname || '') + (window.location?.search || '')
        );

        try { persistor?.pause(); } catch { /* pause is best-effort */ }

        try {
            await userManager.removeUser();
        } catch (e) {
            console.warn('[auth] removeUser failed:', e);
        }

        try {
            await persistor?.purge();
        } catch (e) {
            console.warn('[auth] persistor.purge failed:', e);
        }

        try {
            await userManager.clearStaleState();
        } catch {
            // ignore — stale state cleanup is advisory
        }

        try {
            await userManager.signinRedirect({ state: { returnTo } });
        } catch (e) {
            console.error('[auth] signinRedirect failed:', e);
            if (typeof window !== 'undefined' && window.location) {
                window.location.assign('/');
            }
        }
    })();

    return forceLogoutPromise;
}
